import { QueryClient } from '@tanstack/react-query';

// Development ortamında cache devre dışı - Her zaman fresh data
const isDevelopment = process.env.NODE_ENV === 'development';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Development'ta cache devre dışı, production'da agresif cache
      staleTime: isDevelopment ? 0 : 2 * 60 * 60 * 1000,
      // Development'ta cache'de tutma süresi minimum, production'da 24 saat
      gcTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000,
      // Retry sayısı - development'ta 3, production'da 0
      retry: isDevelopment ? 3 : 0,
      // Retry delay - development'ta daha uzun
      retryDelay: isDevelopment ? 2000 : 100,
      // Development'ta her zaman refetch, production'da kapalı
      refetchOnWindowFocus: isDevelopment ? true : false,
      // Development'ta reconnect'te refetch, production'da kapalı
      refetchOnReconnect: isDevelopment ? true : false,
      // Development'ta mount'ta refetch, production'da kapalı
      refetchOnMount: isDevelopment ? true : false,
      // Development'ta online-first, production'da offline-first
      networkMode: isDevelopment ? 'online' : 'offlineFirst',
      // Development'ta cache time minimum, production'da maksimum
      cacheTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000,
      // Development'ta stale while revalidate kapalı, production'da açık
      staleWhileRevalidate: isDevelopment ? false : true,
      // Development'ta keep previous data kapalı, production'da açık
      keepPreviousData: isDevelopment ? false : true,
    },
    mutations: {
      // Mutation retry sayısı - development'ta 3, production'da 0
      retry: isDevelopment ? 3 : 0,
      // Network mode
      networkMode: 'online',
    },
  },
});

// Cache key'leri
export const CACHE_KEYS = {
  // FAQ kategorileri
  FAQ_CATEGORIES: 'faq-categories',
  // FAQ makaleleri (kategori bazında)
  FAQ_ARTICLES: (categoryId) => ['faq-articles', categoryId],
  // FAQ makaleleri (folder bazında)
  FAQ_FOLDER_ARTICLES: (categoryId, folderId) => ['faq-folder-articles', categoryId, folderId],
  // Tekil makale
  FAQ_ARTICLE: (articleId) => ['faq-article', articleId],
  // Arama sonuçları
  SEARCH_RESULTS: (query) => ['search-results', query],
  // Tüm FAQ verisi
  ALL_FAQ_DATA: (language) => ['all-faq-data', language],
  // Kategoriler ve klasörler (yeni optimize edilmiş endpoint)
  CATEGORIES_WITH_FOLDERS: (language) => ['categories-with-folders', language],
  // Klasör makaleleri (yeni optimize edilmiş endpoint)
  FOLDER_ARTICLES: (folderId, language) => ['folder-articles', folderId, language],
  // Makale (yeni optimize edilmiş endpoint)
  ARTICLE_WITH_LOCALE: (articleId, language) => ['article-with-locale', articleId, language],
  // Popüler makaleler
  POPULAR_ARTICLES: (language) => ['popular-articles', language],
  // Son görüntülenen makaleler
  RECENT_ARTICLES: 'recent-articles',
  // Kullanıcı tercihleri
  USER_PREFERENCES: 'user-preferences',
  // Desteklenen diller
  LOCALES: 'locales',
};

// Gelişmiş cache yardımcı fonksiyonları
export const cacheUtils = {
  // Belirli bir kategori cache'ini temizle
  invalidateCategory: (categoryId) => {
    queryClient.invalidateQueries({
      queryKey: CACHE_KEYS.FAQ_ARTICLES(categoryId),
    });
  },

  // Belirli bir folder cache'ini temizle
  invalidateFolder: (categoryId, folderId) => {
    queryClient.invalidateQueries({
      queryKey: CACHE_KEYS.FAQ_FOLDER_ARTICLES(categoryId, folderId),
    });
  },

  // Tüm FAQ cache'ini temizle
  invalidateAllFaq: () => {
    queryClient.invalidateQueries({
      queryKey: ['faq'],
    });
  },

  // Belirli bir makale cache'ini temizle
  invalidateArticle: (articleId) => {
    queryClient.invalidateQueries({
      queryKey: CACHE_KEYS.FAQ_ARTICLE(articleId),
    });
  },

  // Arama cache'ini temizle
  invalidateSearch: (query) => {
    queryClient.invalidateQueries({
      queryKey: CACHE_KEYS.SEARCH_RESULTS(query),
    });
  },

  // Cache'den veri al
  getCachedData: (key) => {
    return queryClient.getQueryData(key);
  },

  // Cache'e veri kaydet
  setCachedData: (key, data) => {
    queryClient.setQueryData(key, data);
  },

  // Tüm cache'i temizle (sadece acil durumlar için)
  clearAllCache: () => {
    queryClient.clear();
  },

  // Cache istatistiklerini al
  getCacheStats: () => {
    const queries = queryClient.getQueryCache().getAll();
    return {
      totalQueries: queries.length,
      activeQueries: queries.filter(q => q.isActive()).length,
      staleQueries: queries.filter(q => q.isStale()).length,
      fetchingQueries: queries.filter(q => q.isFetching()).length,
    };
  },

  // Cache'i localStorage'a kaydet (persistent cache)
  persistCache: () => {
    const queries = queryClient.getQueryCache().getAll();
    const cacheData = queries.map(query => ({
      key: query.queryKey,
      data: query.state.data,
      timestamp: Date.now(),
    }));
    
    try {
      localStorage.setItem('faq-cache', JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Cache persistence failed:', error);
    }
  },

  // Cache'i localStorage'dan yükle
  loadPersistedCache: () => {
    try {
      const cacheData = localStorage.getItem('faq-cache');
      if (cacheData) {
        const parsed = JSON.parse(cacheData);
        parsed.forEach(item => {
          // 24 saatten eski cache'leri yükleme
          if (Date.now() - item.timestamp < 24 * 60 * 60 * 1000) {
            queryClient.setQueryData(item.key, item.data);
          }
        });
      }
    } catch (error) {
      console.warn('Cache loading failed:', error);
    }
  },

  // Prefetch stratejisi - agresif önceden yükleme
  aggressivePrefetch: (queries) => {
    queries.forEach(query => {
      queryClient.prefetchQuery({
        ...query,
        staleTime: 2 * 60 * 60 * 1000, // 2 saat
        gcTime: 24 * 60 * 60 * 1000, // 24 saat
      });
    });
  },
}; 